import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Check, X, Lock, ArrowRight, CircleDollarSign } from "lucide-react";
import { MaskedLine, FadeUp, EASE } from "@/components/motion";
import api, { formatApiError } from "@/lib/api";
import { buildParams, setParams, getSession } from "@/lib/track";

const STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia","Florida",
  "Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts",
  "Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina",
  "South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming",
];

const REDIRECT_SECONDS = 15;

const Label = ({ children }) => (
  <label className="text-xs font-semibold text-[#0F1F1A] mb-1.5 block">
    {children} <span className="text-[#D92D20]">*</span>
  </label>
);

const FieldError = ({ id, msg }) =>
  msg ? <p className="text-[#D92D20] text-[11px] font-mono2 mt-1.5" data-testid={id}>{msg}</p> : null;

export default function PersonalLoan() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState(25000);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", state: "", zip: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [qs, setQs] = useState("");
  const [count, setCount] = useState(REDIRECT_SECONDS);
  const timer = useRef(null);

  useEffect(() => {
    if (!showModal) return;
    setCount(REDIRECT_SECONDS);
    timer.current = setInterval(() => {
      setCount((c) => {
        if (c <= 1) {
          clearInterval(timer.current);
          navigate(`/identity-protection${qs ? "?" + qs : ""}`);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [showModal, navigate]);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };
  const setDigits = (k, max) => (e) => {
    setForm({ ...form, [k]: e.target.value.replace(/\D/g, "").slice(0, max) });
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (form.phone.length !== 10) errs.phone = "Enter exactly 10 digits (numbers only)";
    if (form.zip.length !== 5) errs.zip = "Enter exactly 5 digits";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await api.post("/leads", {
        ...form,
        service: "Personal Loan",
        amount: `$${amount.toLocaleString()}`,
        message: "",
      });
      const params = buildParams({
        fn: form.first_name,
        ln: form.last_name,
        email: form.email,
        phone: form.phone,
        state: form.state,
        zip: form.zip,
      });
      setParams(params);
      setQs(params);
      api.post("/track/event", {
        session: getSession(),
        type: "form_submit",
        page: "/personal-loan",
        target: `loan-form $${amount.toLocaleString()}`,
        customer: `${form.first_name} ${form.last_name} · ${form.email}`,
      }).catch(() => {});
      setShowModal(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const goNow = () => {
    clearInterval(timer.current);
    navigate(`/identity-protection${qs ? "?" + qs : ""}`);
  };
  const closeModal = () => {
    clearInterval(timer.current);
    setShowModal(false);
  };

  return (
    <div data-testid="personal-loan-page">
      <section className="px-6 pt-40 pb-24 min-h-screen">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-16 items-start">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#0C6B4F] mb-8">
              <MaskedLine delay={0.1}>Personal Loan</MaskedLine>
            </p>
            <h1 className="font-display font-light text-5xl md:text-6xl tracking-tight leading-[1.02] text-[#0F1F1A]">
              <MaskedLine delay={0.25}>Your offer,</MaskedLine>
              <MaskedLine delay={0.45}><span className="italic text-[#0C6B4F]">personalized.</span></MaskedLine>
            </h1>
            <FadeUp delay={0.7} className="mt-8 flex flex-col gap-8">
              <p className="text-[#5C6B62] leading-relaxed max-w-md">
                One request, multiple vetted lenders. Tell us the amount and where you live — we compare the network and match you with offers that fit your profile.
              </p>
              <div className="flex flex-col gap-3 font-mono2 text-xs uppercase tracking-[0.2em] text-[#8A978D]">
                <span>$1,000 – $50,000 requests</span>
                <span>Any credit profile welcome</span>
                <span>Funds as fast as next business day</span>
                <span>No hard inquiry to compare</span>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={0.3}>
            <div className="bg-white border border-[#DFE5DC] rounded-3xl shadow-[0_30px_80px_-24px_rgba(15,61,46,0.22)] p-7 md:p-9" data-testid="loan-form-card">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-2xl bg-[#0C6B4F] text-white flex items-center justify-center shadow-lg shadow-[#0C6B4F]/30">
                  <CircleDollarSign className="w-6 h-6" strokeWidth={1.75} />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-[#0F1F1A]">Get Personalized Loan Offers</h3>
                  <p className="text-xs text-[#5C6B62]">Compare rates from multiple lenders in minutes</p>
                </div>
              </div>

              <form onSubmit={submit} noValidate data-testid="loan-form" className="flex flex-col gap-5">
                <div>
                  <div className="flex justify-between items-end mb-3">
                    <span className="text-sm font-semibold text-[#0F1F1A]">Loan Amount</span>
                    <span className="font-display text-3xl text-[#0C6B4F]" data-testid="loan-amount-value">${amount.toLocaleString()}</span>
                  </div>
                  <input
                    type="range"
                    min="1000"
                    max="50000"
                    step="500"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    data-testid="loan-amount-slider"
                    className="w-full accent-[#0C6B4F]"
                  />
                  <div className="flex justify-between font-mono2 text-[10px] text-[#8A978D] mt-1">
                    <span>$1,000</span>
                    <span>$50,000</span>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <input className="a-input" required value={form.first_name} onChange={set("first_name")} data-testid="loan-first-name" />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <input className="a-input" required value={form.last_name} onChange={set("last_name")} data-testid="loan-last-name" />
                  </div>
                </div>

                <div>
                  <Label>Email Address</Label>
                  <input className="a-input" type="email" required value={form.email} onChange={set("email")} data-testid="loan-email" />
                  <FieldError id="loan-email-error" msg={errors.email} />
                </div>

                <div>
                  <Label>Phone Number <span className="font-normal text-[#8A978D]">(10 digits)</span></Label>
                  <input className="a-input" type="tel" required inputMode="numeric" maxLength={10} pattern="[0-9]{10}" title="Enter a 10-digit phone number" value={form.phone} onChange={setDigits("phone", 10)} data-testid="loan-phone" />
                  <FieldError id="loan-phone-error" msg={errors.phone} />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>State</Label>
                    <select className="a-input" required value={form.state} onChange={set("state")} data-testid="loan-state">
                      <option value="" disabled>Select state</option>
                      {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>ZIP Code <span className="font-normal text-[#8A978D]">(5 digits)</span></Label>
                    <input className="a-input" required inputMode="numeric" maxLength={5} pattern="[0-9]{5}" title="Enter a 5-digit ZIP code" value={form.zip} onChange={setDigits("zip", 5)} data-testid="loan-zip" />
                    <FieldError id="loan-zip-error" msg={errors.zip} />
                  </div>
                </div>

                <label className="flex items-start gap-3 text-xs text-[#5C6B62] leading-relaxed cursor-pointer" data-testid="loan-consent-label">
                  <input
                    type="checkbox"
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="loan-consent"
                    className="mt-0.5 accent-[#0C6B4F] w-4 h-4 shrink-0"
                  />
                  <span>
                    By submitting this form, I agree to the <Link to="/disclosures#terms" className="underline text-[#0C6B4F]">Terms of Service</Link>,{" "}
                    <Link to="/privacy" className="underline text-[#0C6B4F]">Privacy Policy</Link>, and consent to receive communications.{" "}
                    <span className="text-[#D92D20]">*</span>
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="loan-submit-button"
                  className="flex items-center justify-center gap-2 bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#0A5C43] active:scale-[0.99] disabled:opacity-60 transition-[background-color,transform,opacity] duration-300"
                >
                  {loading ? "Comparing Offers…" : "Compare My Offers"} <ArrowRight className="w-4 h-4" />
                </button>

                <p className="flex items-center justify-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-[#8A978D]">
                  <Lock className="w-3.5 h-3.5" /> Your information is secure and encrypted
                </p>
              </form>
            </div>
          </FadeUp>
        </div>
      </section>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/50 backdrop-blur-sm"
            data-testid="loan-success-modal"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="relative w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl"
            >
              <button onClick={closeModal} data-testid="modal-close-button" className="absolute top-4 right-4 text-white/80 hover:text-white z-10 transition-colors duration-300" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
              <div className="bg-gradient-to-br from-[#0C6B4F] to-[#0A5C43] px-8 py-10 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 12 }}
                  className="w-16 h-16 mx-auto mb-5 rounded-full bg-white/15 border border-white/30 flex items-center justify-center"
                >
                  <Check className="w-8 h-8 text-white" strokeWidth={2.5} />
                </motion.div>
                <h3 className="font-display text-3xl text-white mb-3">We've received your details!</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  Thank you for choosing Trusted Financial Hub. Our team will match you with the best loan offers shortly.
                </p>
              </div>
              <div className="p-8">
                <div className="bg-[#EDF2EA] border border-[#DFE5DC] rounded-2xl p-5 mb-6">
                  <p className="text-sm text-[#0F1F1A] leading-relaxed">
                    <span className="font-semibold">Next step:</span> While you wait, we recommend you check your{" "}
                    <span className="font-semibold">Credit Health</span> to stay up-to-date with your credit history and protect it against fraud.
                  </p>
                </div>
                <div className="h-1 rounded-full bg-[#EDF2EA] overflow-hidden mb-3">
                  <motion.div
                    className="h-full bg-[#0C6B4F]"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: REDIRECT_SECONDS, ease: "linear" }}
                  />
                </div>
                <p className="text-center text-xs text-[#5C6B62] mb-5">
                  Redirecting to <span className="font-semibold text-[#0C6B4F]">Credit Health</span> in{" "}
                  <span className="font-mono2 font-semibold" data-testid="redirect-countdown">{count}s</span>
                </p>
                <button
                  onClick={goNow}
                  data-testid="credit-health-now-button"
                  className="w-full flex items-center justify-center gap-2 bg-[#0C6B4F] text-white text-xs font-semibold uppercase tracking-[0.2em] py-4 rounded-full hover:bg-[#0A5C43] active:scale-[0.99] transition-[background-color,transform] duration-300"
                >
                  Check My Credit Health Now <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
